// src/pages/ProfilePage.js
// Navigator settings — profile edit + change password
// Uses NavigatorManagementService endpoints via api.js

import React, { useState } from 'react';
import { AppShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useNavigatorProfile } from '../context/NavigatorContext';
import { navigatorAPI } from '../services/api';
import { fetchCurrentNavigator, resolveNavigatorId } from '../utils/currentNavigator';

import Icon from '../components/Icon';
const ROLE_LABELS = {
  ADMIN:            'Admin',
  HEALTH_NAVIGATOR: 'Health Navigator',
  DOCTOR:           'Doctor',
  SUPPORT_STAFF:    'Support Staff',
};

const GENDER_LABELS = { MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' };

export default function ProfilePage() {
  const { user, login } = useAuth();
  const { theme, refreshProfile: refreshTheme } = useNavigatorProfile();

  const [tab,     setTab]     = useState('profile');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [showPw,  setShowPw]  = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);

  // Profile edit form
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName:  user?.lastName  || '',
    phone:     user?.phone     || '',
    gender:    '',
  });

  // The login response (and therefore `user`) never carries phone or gender
  // — those only exist on the full navigator record, so fetch it on mount
  // rather than showing blanks until an edit happens to patch them in.
  const [navigatorProfile, setNavigatorProfile] = useState(null);

  React.useEffect(() => {
    if (!user) return;
    // fetchCurrentNavigator returns the whole row, so this no longer resolves
    // an id and then GETs it — that second call went to /navigators/{id},
    // which is admin-only, and a navigator reading their OWN profile is not an
    // admin operation.
    fetchCurrentNavigator()
      .then(me => {
        setNavigatorProfile(me);
        setProfileForm(f => ({
          ...f,
          firstName: me.firstName || '',
          lastName:  me.lastName  || '',
          phone:     me.phone     || '',
          gender:    me.gender    || '',
        }));
      })
      .catch(() => { /* keep the blanks from `user` — not fatal */ });
  }, [user?.id, user?.email]);

  // Password form
  const [pwForm, setPwForm] = useState({
    currentPassword: '',
    newPassword:     '',
    confirmPassword: '',
  });

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'NA';
  const setProfile = k => e => setProfileForm(f => ({ ...f, [k]: e.target.value }));
  const setPw      = k => e => setPwForm(f => ({ ...f, [k]: e.target.value }));

  // ── Update profile ────────────────────────────────────────────────────────
  const handleUpdateProfile = async e => {
    e.preventDefault();
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      setError('First and last name are required.'); return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      const userId = await resolveNavigatorId(user);
      const res = await navigatorAPI.update(userId, {
        firstName: profileForm.firstName.trim(),
        lastName:  profileForm.lastName.trim(),
        phone:     profileForm.phone.trim() || null,
        gender:    profileForm.gender || null,
      });
      // Update previu_user in localStorage so AuthContext reflects changes immediately
      const updated = res.data;
      const stored  = JSON.parse(localStorage.getItem('previu_user') || '{}');
      localStorage.setItem('previu_user', JSON.stringify({
        ...stored,
        firstName: updated.firstName,
        lastName:  updated.lastName,
        name:      updated.name || `${updated.firstName} ${updated.lastName}`,
        phone:     updated.phone,
      }));
      // The response already carries the saved gender/phone — use it directly
      // instead of a second fetch, so the Profile Details tab reflects the
      // change immediately too.
      setNavigatorProfile(updated);
      setProfileForm({
        firstName: updated.firstName || '',
        lastName:  updated.lastName  || '',
        phone:     updated.phone     || '',
        gender:    updated.gender    || '',
      });
      setSuccess('Profile updated successfully. Refresh the page to see changes in the header.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally { setSaving(false); }
  };

  // ── Change password ───────────────────────────────────────────────────────
  const handleChangePassword = async e => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setError('Passwords do not match.'); return;
    }
    if (pwForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      const userId = await resolveNavigatorId(user);
      await navigatorAPI.changePassword(userId, {
        currentPassword: pwForm.currentPassword,
        newPassword:     pwForm.newPassword,
      });
      setSuccess('Password changed successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password. Check your current password.');
    } finally { setSaving(false); }
  };

  // ── Appearance (theme) ────────────────────────────────────────────────────
  const handleThemeChange = async next => {
    if (next === theme || themeSaving) return;
    setThemeSaving(true); setError(''); setSuccess('');
    try {
      const userId = await resolveNavigatorId(user);
      await navigatorAPI.update(userId, { theme: next });
      refreshTheme();
      setSuccess('Appearance updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update appearance.');
    } finally { setThemeSaving(false); }
  };

  function switchTab(t) { setTab(t); setError(''); setSuccess(''); }

  return (
    <AppShell breadcrumbs={[{ label: 'Profile & Settings' }]}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Profile & Settings</div>
          <div className="page-subtitle">Manage your account details and security</div>
        </div>
      </div>

      {/* Hero card */}
      <div className="fade-in-up delay-1" style={{
        background: 'linear-gradient(135deg, var(--brand-dark) 0%, var(--brand-navy) 100%)',
        borderRadius: 'var(--radius-lg)', padding: '28px 32px',
        marginBottom: 24, display: 'flex', alignItems: 'center', gap: 22,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -30, top: -30, width: 160,
          height: 160, borderRadius: '50%', background: 'rgba(184,132,58,0.1)' }} />
        <div style={{
          width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, var(--brand-accent), var(--amber-text))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, fontWeight: 800, color: 'var(--text-on-brand-dark)',
          boxShadow: '0 0 0 3px rgba(184,132,58,0.3)',
        }}>{initials}</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 24,
            fontWeight: 700, color: 'var(--text-on-brand-dark)', marginBottom: 4 }}>
            {user?.name}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
            {user?.email}
          </div>
          <span style={{
            background: 'rgba(184,132,58,0.2)', border: '1px solid rgba(184,132,58,0.4)',
            borderRadius: 99, padding: '3px 12px', fontSize: 12,
            fontWeight: 700, color: 'var(--brand-accent)', letterSpacing: 0.5,
          }}>
            {ROLE_LABELS[user?.role] || user?.role}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-bar fade-in-up delay-2">
        <button className={`tab-btn ${tab === 'profile'  ? 'active' : ''}`} onClick={() => switchTab('profile')}>
          Profile Details
        </button>
        <button className={`tab-btn ${tab === 'edit'     ? 'active' : ''}`} onClick={() => switchTab('edit')}>
          Edit Profile
        </button>
        <button className={`tab-btn ${tab === 'password' ? 'active' : ''}`} onClick={() => switchTab('password')}>
          Change Password
        </button>
        {(
          <button className={`tab-btn ${tab === 'appearance' ? 'active' : ''}`} onClick={() => switchTab('appearance')}>
            Appearance
          </button>
        )}
      </div>

      {/* Alerts */}
      {error   && <div className="alert alert-error"   style={{ margin: '16px 0' }}><Icon name="alert" size={14} /> {error}</div>}
      {success && <div className="alert alert-success" style={{ margin: '16px 0' }}>✓ {success}</div>}

      {/* ── Tab: Profile Details ── */}
      {tab === 'profile' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}
          className="fade-in-up">
          <div className="card">
            <div className="card-title">Personal Information</div>
            <InfoRow label="First Name" value={user?.firstName} />
            <InfoRow label="Last Name"  value={user?.lastName} />
            <InfoRow label="Full Name"  value={user?.name} />
            <InfoRow label="Email"      value={user?.email} />
            <InfoRow label="Phone"      value={navigatorProfile?.phone} />
            <InfoRow label="Gender"     value={GENDER_LABELS[navigatorProfile?.gender] || navigatorProfile?.gender} />
          </div>
          <div className="card">
            <div className="card-title">Account Details</div>
            <InfoRow label="Role"     value={ROLE_LABELS[user?.role] || user?.role} />
            <InfoRow label="Account"  value="Active" highlight />
            <InfoRow label="Platform" value="Previu Health Navigator" />
          </div>
        </div>
      )}

      {/* ── Tab: Edit Profile ── */}
      {tab === 'edit' && (
        <div className="form-section fade-in-up" style={{ maxWidth: 480 }}>
          <div className="form-section-title">Edit Profile</div>
          <form onSubmit={handleUpdateProfile}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">First Name <span className="required">*</span></label>
                  <input className="form-input" style={{ width: '100%' }}
                    type="text" value={profileForm.firstName}
                    onChange={setProfile('firstName')}
                    placeholder="First name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name <span className="required">*</span></label>
                  <input className="form-input" style={{ width: '100%' }}
                    type="text" value={profileForm.lastName}
                    onChange={setProfile('lastName')}
                    placeholder="Last name" required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" style={{ width: '100%' }}
                  type="tel" value={profileForm.phone}
                  onChange={setProfile('phone')}
                  placeholder="e.g. +919876543210" />
              </div>
              <div className="form-group">
                <label className="form-label">Gender</label>
                <select className="form-select" style={{ width: '100%' }}
                  value={profileForm.gender} onChange={setProfile('gender')}>
                  <option value="">Select gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" style={{ width: '100%', opacity: 0.6 }}
                  type="email" value={user?.email || ''} disabled
                  title="Email cannot be changed" />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Email cannot be changed. Contact admin if needed.
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : '✓ Save Changes'}
              </button>
              <button className="btn btn-ghost" type="button"
                onClick={() => setProfileForm({
                  firstName: navigatorProfile?.firstName || user?.firstName || '',
                  lastName:  navigatorProfile?.lastName  || user?.lastName  || '',
                  phone:     navigatorProfile?.phone      || '',
                  gender:    navigatorProfile?.gender     || '',
                })}>
                Reset
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Tab: Change Password ── */}
      {tab === 'password' && (
        <div className="form-section fade-in-up" style={{ maxWidth: 480 }}>
          <div className="form-section-title">Change Password</div>
          <form onSubmit={handleChangePassword}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Current Password <span className="required">*</span></label>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" style={{ width: '100%', paddingRight: 40 }}
                    type={showPw ? 'text' : 'password'}
                    value={pwForm.currentPassword} onChange={setPw('currentPassword')}
                    placeholder="Your current password" required />
                  <button type="button" onClick={() => setShowPw(p => !p)}
                    style={{ position: 'absolute', right: 10, top: '50%',
                      transform: 'translateY(-50%)', background: 'none',
                      border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">New Password <span className="required">*</span></label>
                <input className="form-input" style={{ width: '100%' }}
                  type={showPw ? 'text' : 'password'}
                  value={pwForm.newPassword} onChange={setPw('newPassword')}
                  placeholder="Min 8 characters" required minLength={8} />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password <span className="required">*</span></label>
                <input className="form-input" style={{ width: '100%' }}
                  type={showPw ? 'text' : 'password'}
                  value={pwForm.confirmPassword} onChange={setPw('confirmPassword')}
                  placeholder="Repeat new password" required />
                {pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword && (
                  <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                    Passwords do not match
                  </div>
                )}
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" type="submit"
                disabled={saving || (pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword)}>
                {saving ? 'Saving…' : '✓ Change Password'}
              </button>
              <button className="btn btn-ghost" type="button"
                onClick={() => setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' })}>
                Clear
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Tab: Appearance ── */}
      {tab === 'appearance' && (
        <div className="form-section fade-in-up" style={{ maxWidth: 560 }}>
          <div className="form-section-title">Appearance</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            Choose how the Previu portal looks for you. Dark is the default.
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {[
              { value: 'DARK', label: 'Dark',
                desc: 'The default — a dark color palette.' },
              { value: 'LIGHT', label: 'Light',
                desc: 'A bright, warm-toned portal.' },
            ].map(opt => {
              const selected = theme === opt.value;
              return (
                <div
                  key={opt.value}
                  className="card"
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-disabled={themeSaving}
                  onClick={() => handleThemeChange(opt.value)}
                  onKeyDown={e => {
                    if (!themeSaving && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleThemeChange(opt.value);
                    }
                  }}
                  style={{
                    flex: '1 1 220px', marginBottom: 0,
                    cursor: themeSaving ? 'not-allowed' : 'pointer',
                    border: `2px solid ${selected ? 'var(--brand-accent)' : 'var(--border)'}`,
                    opacity: themeSaving && !selected ? 0.6 : 1,
                    transition: 'border-color 0.15s',
                  }}
                >
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                    color: 'var(--text-primary)', marginBottom: 6,
                  }}>
                    {selected ? '✓ ' : ''}{opt.label}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {opt.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </AppShell>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 12,
      padding: '8px 0', borderBottom: '1px solid var(--bg-subtle)', fontSize: 13,
    }}>
      <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: 11,
        textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>{label}</span>
      {/* Same overflow fix as PatientDetailPage.js's InfoRow (a separate,
          duplicated component): minWidth: 0 lets the value shrink past its
          own content width, maxWidth caps it at roughly half the row, and
          overflowWrap breaks a token — an email address, mainly — that has
          no natural wrap point. Without all three a long email pushed clean
          past the card's edge instead of wrapping onto a second line. */}
      <span style={{ fontWeight: 600, color: highlight ? 'var(--green)' : 'var(--text-primary)',
        textAlign: 'right', maxWidth: '60%', minWidth: 0, overflowWrap: 'anywhere' }}>
        {value || '—'}
      </span>
    </div>
  );
}
